import React from 'react';
import { Inbox } from 'lucide-react';
import { cn } from '../lib/utils';

// Empty state component
export interface EmptyStateProps {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick?: () => void;
    href?: string;
  };
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
}) => {
  const ActionWrapper = action?.href ? 'a' : 'button';

  return (
    <div className={cn(
      'flex flex-col items-center justify-center text-center py-16 px-6',
      className
    )}>
      <div className="w-16 h-16 rounded-full bg-surface-2 flex items-center justify-center mb-6">
        <Icon className="w-8 h-8 text-text-muted" />
      </div>
      <h3 className="font-display text-xl font-semibold text-text-primary mb-2">
        {title}
      </h3>
      <p className="text-text-secondary max-w-sm mb-6">
        {description}
      </p>
      {action && (
        <ActionWrapper
          href={action.href}
          onClick={action.onClick}
          className="px-6 py-2.5 rounded-vault bg-gold-500 hover:bg-gold-400 text-black font-medium transition-colors"
        >
          {action.label}
        </ActionWrapper>
      )}
    </div>
  );
};

// Tabs component
export interface Tab {
  id?: string;
  value?: string;
  label: string;
  badge?: number;
  count?: number;
}

export interface TabsProps {
  value?: string;
  activeTab?: string;
  onChange?: (value: string) => void;
  onTabChange?: (value: string) => void;
  tabs?: Tab[];
  items?: Tab[];
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  value,
  activeTab,
  onChange,
  onTabChange,
  tabs,
  items,
  className,
}) => {
  const tabList = tabs || items || [];
  const activeValue = value || activeTab;
  const handleChange = onChange || onTabChange;

  return (
    <div className={cn(
      'flex gap-1 p-1 rounded-vault-lg bg-surface-2/50 border border-white/5 overflow-x-auto',
      className
    )}>
      {tabList.map((tab) => {
        const tabValue = tab.value || tab.id || '';
        const badgeValue = tab.badge ?? tab.count;
        return (
          <button
            key={tabValue}
            onClick={() => handleChange?.(tabValue)}
            className={cn(
              'flex-1 min-w-max flex items-center justify-center gap-2 px-4 py-2 rounded-vault text-sm font-medium transition-colors whitespace-nowrap',
              activeValue === tabValue
                ? 'bg-surface-3 text-text-primary'
                : 'text-text-muted hover:text-text-secondary'
            )}
          >
            {tab.label}
            {badgeValue !== undefined && (
              <span className={cn(
                'px-1.5 py-0.5 rounded-full text-xs tabular-nums',
                activeValue === tabValue
                  ? 'bg-gold-500/20 text-gold-500'
                  : 'bg-surface-3 text-text-muted'
              )}>
                {badgeValue}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
